import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  input,
  Output,
  Signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { getCurrencyByRegion } from '@principle-theorem/accounting';
import {
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import { BillingMeter } from '@principle-theorem/principle-core';
import {
  BillingMeterId,
  BillingMeterPriceLookupId,
  IBaseSMSIntegrationData,
  IBillingMeterOption,
} from '@principle-theorem/principle-core/interfaces';
import { filterUndefined, Region } from '@principle-theorem/shared';
import { first } from 'lodash';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'pr-stripe-sms-pricing-selector',
  templateUrl: './stripe-sms-pricing-selector.component.html',
  styleUrl: './stripe-sms-pricing-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, MatInputModule, MatSelectModule],
})
export class StripeSMSPricingSelectorComponent {
  form = new TypedFormGroup<
    Omit<IBaseSMSIntegrationData, 'inboundCost' | 'outboundCost'>
  >({
    inboundPriceId: new TypedFormControl<BillingMeterPriceLookupId>(
      undefined,
      Validators.required
    ),
    outboundPriceId: new TypedFormControl<BillingMeterPriceLookupId>(
      undefined,
      Validators.required
    ),
    marketingPriceId: new TypedFormControl<BillingMeterPriceLookupId>(
      undefined,
      Validators.required
    ),
  });
  readOnly = input(false, {
    transform: coerceBooleanProperty,
  });
  region = input.required<Region>();
  selectedPrices = input.required<IBaseSMSIntegrationData>();
  @Output() pricingChanged = new EventEmitter<IBaseSMSIntegrationData>();

  currency = computed(() => getCurrencyByRegion(this.region()));
  inboundPrices: Signal<IBillingMeterOption[]>;
  outboundPrices: Signal<IBillingMeterOption[]>;
  marketingPrices: Signal<IBillingMeterOption[]>;
  priceSummary: Signal<
    Partial<{
      inbound: IBillingMeterOption;
      outbound: IBillingMeterOption;
      marketing: IBillingMeterOption;
    }>
  >;

  constructor() {
    this.inboundPrices = computed(() =>
      BillingMeter.filterByMeterId(
        BillingMeterId.SMSInboundSegments,
        this.region()
      )
    );

    effect(() => {
      const inboundPrices = this.inboundPrices();
      if (inboundPrices.length === 1) {
        this.form.patchValue({
          inboundPriceId: inboundPrices[0].priceLookupId,
        });
      }
    });

    this.outboundPrices = computed(() =>
      BillingMeter.filterByMeterId(
        BillingMeterId.SMSOutboundSegments,
        this.region()
      )
    );

    effect(() => {
      const outboundPrices = this.outboundPrices();
      if (outboundPrices.length === 1) {
        this.form.patchValue({
          outboundPriceId: outboundPrices[0].priceLookupId,
        });
      }
    });

    this.marketingPrices = computed(() =>
      BillingMeter.filterByMeterId(
        BillingMeterId.SMSMarketingSegments,
        this.region()
      )
    );

    combineLatest([
      toObservable(this.selectedPrices),
      toObservable(this.region).pipe(filterUndefined()),
      toObservable(this.readOnly).pipe(filter((readOnly) => !readOnly)),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([selectedPrices, _region]) => {
        const mergedSettings = {
          inboundPriceId: selectedPrices?.inboundPriceId,
          outboundPriceId: selectedPrices?.outboundPriceId,
          marketingPriceId: selectedPrices?.marketingPriceId,
        };

        this.form.patchValue(mergedSettings, { emitEvent: false });
      });

    validFormGroupChanges$(this.form)
      .pipe(takeUntilDestroyed())
      .subscribe((formData) => this.pricingChanged.emit(formData));

    const formSignal = toSignal(validFormGroupChanges$(this.form));

    this.priceSummary = computed(() => {
      const inbound = this.inboundPrices().find(
        (price) => price.priceLookupId === formSignal()?.inboundPriceId
      );
      const outbound = this.outboundPrices().find(
        (price) => price.priceLookupId === formSignal()?.outboundPriceId
      );
      const marketing = this.marketingPrices().find(
        (price) => price.priceLookupId === formSignal()?.marketingPriceId
      );

      return {
        inbound: inbound ?? first(this.inboundPrices()),
        outbound: outbound ?? first(this.outboundPrices()),
        marketing: marketing ?? first(this.marketingPrices()),
      };
    });
  }
}
